import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
    AlertCircle,
    ArrowLeft,
    Banknote,
    CalendarIcon,
    CheckCircle,
    ChevronLeft,
    ChevronRight,
    ClipboardCheck,
    Clock,
    Download,
    FileSpreadsheet,
    Loader2,
    Lock,
    Pencil,
    Star,
    X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';
import { z } from 'zod';
import { trpc } from '~/api/trpc';
import Content from '~/components/layout/Content';
import LocalDateTime from '~/lib/localdatetime';
import { formatCurrency } from '~/lib/utils';
import { useUserStore } from '~/stores/user-store';
import { UserRole } from '~/types/server-types/UserRole';

// ============================================================================
// Constants
// ============================================================================

const TRANSACTION_TYPE_LABELS: Record<string, string> = {
    PAYMENT: 'Barzahlung',
    WITHDRAWAL: 'Entnahme',
    UNDO_WITHDRAWAL: 'Entnahme-Storno',
    TRANSFER_TO_OTHER_CASH_REGISTER: 'Umbuchung (ausgehend)',
    TRANSFER_FROM_OTHER_CASH_REGISTER: 'Umbuchung (eingehend)',
};

const TRANSACTION_TYPE_BADGES: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
    PAYMENT: { variant: 'default' },
    WITHDRAWAL: { variant: 'destructive' },
    UNDO_WITHDRAWAL: { variant: 'destructive' },
    TRANSFER_TO_OTHER_CASH_REGISTER: { variant: 'outline' },
    TRANSFER_FROM_OTHER_CASH_REGISTER: { variant: 'secondary' },
};

// ============================================================================
// CSV Export helpers
// ============================================================================

type TransactionExportRow = {
    date: string;
    storeName: string;
    amount: number;
    invoiceNumber: string;
    runningBalance: number;
};

const CSV_HEADERS = [
    'Zahlungsdatum',
    'Filiale',
    'Betrag',
    'Rechnungsnummer',
    'Kassenbestand',
];

const escapeCsvField = (value: string): string => {
    if (value.includes(';') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
};

const generateTransactionsCsv = (rows: TransactionExportRow[]): string => {
    const header = CSV_HEADERS.map(escapeCsvField).join(';');
    const dataRows = rows.map(row => {
        const dateFormatted = format(new Date(row.date), 'dd.MM.yyyy HH:mm', { locale: de });
        return [
            dateFormatted,
            row.storeName,
            row.amount.toFixed(2).replace('.', ','),
            row.invoiceNumber,
            row.runningBalance.toFixed(2).replace('.', ','),
        ].map(escapeCsvField).join(';');
    });
    return '\uFEFF' + [header, ...dataRows].join('\r\n');
};

const downloadCsv = (csv: string, filename: string) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

// ============================================================================
// Schemas
// ============================================================================

const editCashRegisterSchema = z.object({
    name: z.string().min(1, 'Name ist erforderlich'),
    isActive: z.boolean(),
    requiresDailyCashStatement: z.boolean(),
    isDefaultForStore: z.boolean(),
});

// ============================================================================
// Sub-components
// ============================================================================

type EditCashRegisterDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    cashRegisterId: string;
    storeId: number;
    anotherRegisterIsDefault: boolean;
    currentDefaultName?: string;
    defaultValues: {
        name: string;
        isActive: boolean;
        requiresDailyCashStatement: boolean;
        isDefaultForStore: boolean;
    };
};

const EditCashRegisterDialog = ({ open, onOpenChange, cashRegisterId, storeId, anotherRegisterIsDefault, currentDefaultName, defaultValues }: EditCashRegisterDialogProps) => {
    const trpcUtils = trpc.useUtils();

    const form = useForm<z.infer<typeof editCashRegisterSchema>>({
        resolver: zodResolver(editCashRegisterSchema),
        defaultValues,
    });

    useEffect(() => {
        if (open) {
            form.reset(defaultValues);
        }
    }, [open, defaultValues, form]);

    const updateMutation = trpc.cashRegisters.update.useMutation();
    const setAsDefaultMutation = trpc.cashRegisters.setAsDefault.useMutation();
    const removeDefaultMutation = trpc.cashRegisters.removeDefault.useMutation();

    const isSaving = updateMutation.isPending || setAsDefaultMutation.isPending || removeDefaultMutation.isPending;

    const handleSubmit = form.handleSubmit(async (data) => {
        try {
            await updateMutation.mutateAsync({
                id: cashRegisterId,
                name: data.name,
                isActive: data.isActive,
                requiresDailyCashStatement: data.requiresDailyCashStatement,
            });

            if (data.isDefaultForStore && !defaultValues.isDefaultForStore) {
                await setAsDefaultMutation.mutateAsync({ cashRegisterId });
            } else if (!data.isDefaultForStore && defaultValues.isDefaultForStore) {
                await removeDefaultMutation.mutateAsync({ storeId });
            }

            toast.success('Kasse wurde aktualisiert');
            trpcUtils.cashRegisters.getOne.invalidate({ id: cashRegisterId });
            trpcUtils.cashRegisters.getByStore.invalidate();
            onOpenChange(false);
        } catch (error: any) {
            toast.error(`Fehler: ${error.message}`);
        }
    });

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className='sm:max-w-[400px]'>
                <DialogHeader>
                    <DialogTitle>Kasse bearbeiten</DialogTitle>
                    <DialogDescription>
                        Kasse umbenennen, deaktivieren oder Einstellungen ändern.
                    </DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={handleSubmit} className='space-y-4'>
                        <FormField
                            control={form.control}
                            name='name'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Name der Kasse</FormLabel>
                                    <FormControl>
                                        <Input {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='isActive'
                            render={({ field }) => (
                                <FormItem className='flex items-center justify-between rounded-lg border p-3'>
                                    <div className='space-y-0.5'>
                                        <FormLabel>Aktiv</FormLabel>
                                        <p className='text-sm text-muted-foreground'>
                                            Deaktivierte Kassen können nicht mehr verwendet werden.
                                        </p>
                                    </div>
                                    <FormControl>
                                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='requiresDailyCashStatement'
                            render={({ field }) => (
                                <FormItem className='flex items-center justify-between rounded-lg border p-3'>
                                    <div className='space-y-0.5'>
                                        <FormLabel>Täglicher Kassenabschluss erforderlich</FormLabel>
                                        <p className='text-sm text-muted-foreground'>
                                            Markiert, dass diese Kasse einen täglichen Abschluss benötigt.
                                        </p>
                                    </div>
                                    <FormControl>
                                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='isDefaultForStore'
                            render={({ field }) => (
                                <FormItem className='flex items-center justify-between rounded-lg border p-3'>
                                    <div className='space-y-0.5'>
                                        <FormLabel>Standardkasse</FormLabel>
                                        <p className='text-sm text-muted-foreground'>
                                            {anotherRegisterIsDefault
                                                ? `„${currentDefaultName}" ist bereits die Standardkasse für diesen Standort.`
                                                : 'Diese Kasse als Standardkasse für den Standort festlegen.'}
                                        </p>
                                    </div>
                                    <FormControl>
                                        <Switch
                                            checked={field.value}
                                            onCheckedChange={field.onChange}
                                            disabled={anotherRegisterIsDefault}
                                        />
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                        <DialogFooter>
                            <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
                                Abbrechen
                            </Button>
                            <Button type='submit' disabled={isSaving}>
                                {isSaving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                                Speichern
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
};

// ============================================================================
// Main component
// ============================================================================

const CashRegister = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [page, setPage] = useState(0);
    const [statementsPage, setStatementsPage] = useState(0);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [undoWithdrawalTarget, setUndoWithdrawalTarget] = useState<{ id: string; amountEur: number; reason: string } | null>(null);
    const [isExporting, setIsExporting] = useState(false);
    const [exportFrom, setExportFrom] = useState<Date | undefined>(undefined);
    const [exportTo, setExportTo] = useState<Date | undefined>(undefined);
    const [exportPopoverOpen, setExportPopoverOpen] = useState(false);
    const pageSize = 10;
    const trpcUtils = trpc.useUtils();

    const { role } = useUserStore(
        useShallow((state) => ({
            role: state.user?.role,
        })),
    );

    const isAdminOrOperator = role === UserRole.ADMIN || role === UserRole.OPERATOR;

    const todayStr = LocalDateTime.now('Europe/Berlin').getDisplayDate();

    const { data: cashRegister, isLoading } = trpc.cashRegisters.getOne.useQuery(
        { id: id!, summaryDate: todayStr },
        { enabled: !!id },
    );

    const { data: transactionsData, isLoading: transactionsLoading } = trpc.cashRegisters.getTransactions.useQuery(
        { cashRegisterId: id!, page, pageSize },
        { enabled: !!id },
    );

    const { data: statementsOverview, isLoading: statementsLoading } = trpc.cashRegisters.getStatementOverview.useQuery(
        { cashRegisterId: id!, page: statementsPage, pageSize },
        { enabled: !!id },
    );

    const { data: todayStatement } = trpc.cashRegisters.getStatement.useQuery(
        { cashRegisterId: id!, date: todayStr },
        { enabled: !!id && !!cashRegister?.requiresDailyCashStatement },
    );

    const kassenberichtDownload = trpc.cashRegisters.getCashStatementKassenberichtUrl.useMutation({
        onSuccess: (data) => {
            window.open(data.downloadUrl, '_blank');
        },
        onError: (error) => {
            toast.error(`Fehler beim Herunterladen: ${error.message}`);
        },
    });

    const anlageDownload = trpc.cashRegisters.getCashStatementAnlageUrl.useMutation({
        onSuccess: (data) => {
            window.open(data.downloadUrl, '_blank');
        },
        onError: (error) => {
            toast.error(`Fehler beim Herunterladen: ${error.message}`);
        },
    });

    const undoWithdrawalMutation = trpc.cashRegisters.createUndoWithdrawal.useMutation({
        onSuccess: () => {
            toast.success('Entnahme wurde rückgängig gemacht');
            trpcUtils.cashRegisters.getOne.invalidate();
            trpcUtils.cashRegisters.getTransactions.invalidate();
            trpcUtils.cashRegisters.getBalance.invalidate();
            setUndoWithdrawalTarget(null);
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    // Fetch all cash registers for the store to find the current default name
    const { data: storeRegistersData } = trpc.cashRegisters.getByStore.useQuery(
        { storeId: cashRegister?.storeId ?? 0 },
        { enabled: !!cashRegister?.storeId },
    );

    const currentDefaultCashRegisterName = storeRegistersData?.cashRegisters?.find(
        (cr) => cr.id === storeRegistersData.defaultCashRegisterId
    )?.name;

    const anotherRegisterIsDefault = !!storeRegistersData?.defaultCashRegisterId
        && storeRegistersData.defaultCashRegisterId !== id;

    const handleExport = async () => {
        if (!exportFrom || !exportTo || !id) {
            toast.error('Bitte wähle ein Start- und Enddatum.');
            return;
        }
        setIsExporting(true);
        try {
            const dateFrom = format(exportFrom, 'yyyy-MM-dd');
            const dateTo = format(exportTo, 'yyyy-MM-dd');
            const data = await trpcUtils.cashRegisters.exportTransactionsForCsv.fetch({
                cashRegisterId: id,
                dateFrom,
                dateTo,
            });
            if (data.length === 0) {
                toast.info('Keine Transaktionen im gewählten Zeitraum gefunden.');
                return;
            }
            const csv = generateTransactionsCsv(data as TransactionExportRow[]);
            const filename = `kasse_transaktionen_${dateFrom}_bis_${dateTo}.csv`;
            downloadCsv(csv, filename);
            toast.success(`${data.length} Transaktionen exportiert.`);
            setExportPopoverOpen(false);
        } catch (error) {
            toast.error('Fehler beim Export: ' + (error instanceof Error ? error.message : 'Unbekannter Fehler'));
        } finally {
            setIsExporting(false);
        }
    };

    if (isLoading) {
        return (
            <Content>
                <div className='container mx-auto py-10 max-w-5xl'>
                    <Skeleton className='h-8 w-48 mb-6' />
                    <div className='grid gap-6'>
                        <Skeleton className='h-48' />
                        <Skeleton className='h-32' />
                        <Skeleton className='h-64' />
                    </div>
                </div>
            </Content>
        );
    }

    if (!cashRegister) {
        return (
            <Content>
                <div className='container mx-auto py-10'>
                    <div className='text-center py-12'>
                        <h2 className='text-xl font-semibold mb-2'>Kasse nicht gefunden</h2>
                        <Button variant='outline' onClick={() => navigate('/cash-registers')}>
                            <ArrowLeft className='mr-2 h-4 w-4' />
                            Zurück zu Kassen
                        </Button>
                    </div>
                </div>
            </Content>
        );
    }

    return (
        <Content>
            <div className='container mx-auto py-10 max-w-5xl'>
                {/* Header */}
                <div className='flex items-center gap-4 mb-6'>
                    <Button variant='ghost' size='sm' onClick={() => navigate('/cash-registers')}>
                        <ArrowLeft className='h-4 w-4' />
                    </Button>
                    <div className='flex flex-col flex-1 gap-2'>
                        <div className='flex items-center gap-2'>
                            <h1 className='text-2xl font-bold'>{cashRegister.name}</h1>
                            {cashRegister.isActive ? (
                                <Badge variant='default'>Aktiv</Badge>
                            ) : (
                                <Badge variant='secondary'>Deaktiviert</Badge>
                            )}
                            {cashRegister.requiresDailyCashStatement && (
                                <Badge variant='outline' className='text-xs'>Tagesabschluss</Badge>
                            )}
                            {cashRegister.isDefaultForStore && (
                                <Badge variant='secondary' className='text-xs'>
                                    <Star className='mr-1 h-3 w-3' />
                                    Standardkasse
                                </Badge>
                            )}
                            {isAdminOrOperator && (
                                <Button variant='outline' size='sm' className='ml-1' onClick={() => setShowEditDialog(true)}>
                                    <Pencil />
                                    Bearbeiten
                                </Button>
                            )}
                        </div>
                        <p className='text-sm text-muted-foreground font-mono'>{cashRegister.id}</p>
                    </div>
                    <div className='flex items-center gap-3'>
                        <div className='text-right'>
                            <p className='text-sm text-muted-foreground'>Kassenstand</p>
                            <p className={`text-2xl font-bold ${cashRegister.balanceEur < 0 ? 'text-destructive' : ''}`}>
                                {formatCurrency(cashRegister.balanceEur)}
                            </p>
                        </div>
                    </div>
                </div>

                <div className='grid gap-6 md:grid-cols-2 mb-6'>
                    {/* Cash Register Details */}
                    <Card>
                        <CardHeader>
                            <CardTitle className='flex items-center gap-2'>
                                <Banknote className='h-5 w-5' />
                                Kassendetails
                            </CardTitle>
                        </CardHeader>
                        <CardContent className='space-y-4'>
                            <div className='flex justify-between'>
                                <span className='text-muted-foreground'>Name</span>
                                <span className='font-medium'>{cashRegister.name}</span>
                            </div>
                            <div className='flex justify-between'>
                                <span className='text-muted-foreground'>Filiale</span>
                                <span className='font-medium'>{cashRegister.store.name}</span>
                            </div>
                            <div className='flex justify-between'>
                                <span className='text-muted-foreground'>Status</span>
                                {cashRegister.isActive ? (
                                    <Badge variant='default'>Aktiv</Badge>
                                ) : (
                                    <Badge variant='secondary'>Deaktiviert</Badge>
                                )}
                            </div>
                            <div className='flex justify-between'>
                                <span className='text-muted-foreground'>Kassenstand</span>
                                <span className={`font-bold ${cashRegister.balanceEur < 0 ? 'text-destructive' : ''}`}>
                                    {formatCurrency(cashRegister.balanceEur)}
                                </span>
                            </div>
                            <div className='flex justify-between'>
                                <span className='text-muted-foreground'>Tagesabschluss</span>
                                <span>{cashRegister.requiresDailyCashStatement ? 'Erforderlich' : 'Nicht erforderlich'}</span>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Transaction Summary */}
                    <Card>
                        <CardHeader>
                            <CardTitle className='flex items-center gap-2'>
                                <Clock className='h-5 w-5' />
                                Zusammenfassung (Heute)
                            </CardTitle>
                        </CardHeader>
                        <CardContent className='space-y-3'>
                            {cashRegister.transactionSummary.length === 0 ? (
                                <p className='text-sm text-muted-foreground'>Keine Transaktionen vorhanden.</p>
                            ) : (
                                cashRegister.transactionSummary.map((s) => (
                                    <div key={s.type} className='flex justify-between items-center'>
                                        <div>
                                            <span className='text-sm'>{TRANSACTION_TYPE_LABELS[s.type] ?? s.type}</span>
                                            <span className='text-xs text-muted-foreground ml-1'>({s.count}x)</span>
                                        </div>
                                        <span className={`text-sm font-mono font-medium ${s.totalEur >= 0 ? 'text-green-600' : 'text-destructive'}`}>
                                            {s.totalEur >= 0 ? '+ ' : '− '}{formatCurrency(Math.abs(s.totalEur))}
                                        </span>
                                    </div>
                                ))
                            )}
                        </CardContent>
                    </Card>
                </div>

                {cashRegister.requiresDailyCashStatement && (
                    <Card className='mb-6'>
                        <CardHeader className='flex flex-row items-center justify-between space-y-0'>
                            <CardTitle className='flex items-center gap-2'>
                                <ClipboardCheck className='h-5 w-5' />
                                Tagesabschluss
                            </CardTitle>
                            <Button
                                variant='outline'
                                size='sm'
                                onClick={() => navigate(`/cash-register/${id}/statement`)}
                            >
                                Kassensturz öffnen
                            </Button>
                        </CardHeader>
                        <CardContent>
                            {todayStatement?.latestVersion ? (
                                <div className='space-y-3'>
                                    <div className='flex items-center gap-3'>
                                        {todayStatement.latestVersion.differenceEur === 0 ? (
                                            <CheckCircle className='h-5 w-5 text-green-600' />
                                        ) : (
                                            <AlertCircle className='h-5 w-5 text-destructive' />
                                        )}
                                        <div>
                                            <p className='text-sm font-medium'>
                                                Abschluss vorhanden
                                                {todayStatement.latestVersion.cashStatementNumber && (
                                                    <span className='text-muted-foreground font-normal ml-1'>
                                                        ({todayStatement.latestVersion.cashStatementNumber})
                                                    </span>
                                                )}
                                            </p>
                                            <p className={`text-sm ${todayStatement.latestVersion.differenceEur === 0 ? 'text-green-600' : 'text-destructive'}`}>
                                                Differenz: {formatCurrency(todayStatement.latestVersion.differenceEur)}
                                            </p>
                                        </div>
                                    </div>
                                    {todayStatement.latestVersion.hasKassenbericht && todayStatement.latestVersion.hasAnlage && (
                                        <div className='flex gap-2 ml-8'>
                                            <Button
                                                variant='outline'
                                                size='sm'
                                                onClick={() => kassenberichtDownload.mutate({ versionId: todayStatement.latestVersion!.id })}
                                                disabled={kassenberichtDownload.isPending}
                                            >
                                                {kassenberichtDownload.isPending ? (
                                                    <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />
                                                ) : (
                                                    <Download className='mr-1.5 h-3.5 w-3.5' />
                                                )}
                                                Kassenbericht
                                            </Button>
                                            <Button
                                                variant='outline'
                                                size='sm'
                                                onClick={() => anlageDownload.mutate({ versionId: todayStatement.latestVersion!.id })}
                                                disabled={anlageDownload.isPending}
                                            >
                                                {anlageDownload.isPending ? (
                                                    <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />
                                                ) : (
                                                    <Download className='mr-1.5 h-3.5 w-3.5' />
                                                )}
                                                Anlage
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className='flex items-center gap-3'>
                                    <AlertCircle className='h-5 w-5 text-yellow-500' />
                                    <div>
                                        <p className='text-sm font-medium'>Kein Abschluss heute</p>
                                        <p className='text-sm text-muted-foreground'>
                                            Für heute wurde noch kein Kassensturz durchgeführt.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                <Card>
                    <Tabs defaultValue='transactions'>
                        <CardHeader>
                            <div className='flex items-center justify-between'>
                                <TabsList>
                                    <TabsTrigger value='transactions'>Transaktionen</TabsTrigger>
                                    <TabsTrigger value='statements'>Tagesabschluss</TabsTrigger>
                                </TabsList>
                                <Popover open={exportPopoverOpen} onOpenChange={setExportPopoverOpen}>
                                    <PopoverTrigger asChild>
                                        <Button variant='outline' size='sm'>
                                            <FileSpreadsheet className='h-4 w-4 mr-2' />
                                            Export
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className='w-auto p-4' align='end'>
                                        <div className='space-y-4'>
                                            <div className='text-sm font-medium'>Zeitraum wählen</div>
                                            <div className='flex gap-4'>
                                                <div className='space-y-2'>
                                                    <label className='text-xs text-muted-foreground'>Von</label>
                                                    <Popover>
                                                        <PopoverTrigger asChild>
                                                            <Button variant='outline' className='w-[160px] justify-start text-left font-normal'>
                                                                <CalendarIcon className='h-4 w-4 mr-2' />
                                                                {exportFrom
                                                                    ? format(exportFrom, 'dd.MM.yyyy', { locale: de })
                                                                    : 'Startdatum'}
                                                            </Button>
                                                        </PopoverTrigger>
                                                        <PopoverContent className='w-auto p-0' align='start'>
                                                            <Calendar
                                                                mode='single'
                                                                selected={exportFrom}
                                                                onSelect={setExportFrom}
                                                                locale={de}
                                                                disabled={(date) => exportTo ? date > exportTo : false}
                                                            />
                                                        </PopoverContent>
                                                    </Popover>
                                                </div>
                                                <div className='space-y-2'>
                                                    <label className='text-xs text-muted-foreground'>Bis</label>
                                                    <Popover>
                                                        <PopoverTrigger asChild>
                                                            <Button variant='outline' className='w-[160px] justify-start text-left font-normal'>
                                                                <CalendarIcon className='h-4 w-4 mr-2' />
                                                                {exportTo
                                                                    ? format(exportTo, 'dd.MM.yyyy', { locale: de })
                                                                    : 'Enddatum'}
                                                            </Button>
                                                        </PopoverTrigger>
                                                        <PopoverContent className='w-auto p-0' align='start'>
                                                            <Calendar
                                                                mode='single'
                                                                selected={exportTo}
                                                                onSelect={setExportTo}
                                                                locale={de}
                                                                disabled={(date) => exportFrom ? date < exportFrom : false}
                                                            />
                                                        </PopoverContent>
                                                    </Popover>
                                                </div>
                                            </div>
                                            <Button
                                                className='w-full'
                                                onClick={handleExport}
                                                disabled={!exportFrom || !exportTo || isExporting}
                                            >
                                                {isExporting ? (
                                                    <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                                                ) : (
                                                    <Download className='h-4 w-4 mr-2' />
                                                )}
                                                {isExporting ? 'Exportiere...' : 'CSV herunterladen'}
                                            </Button>
                                        </div>
                                    </PopoverContent>
                                </Popover>
                            </div>
                        </CardHeader>

                        {/* Transactions Tab */}
                        <TabsContent value='transactions'>
                            <CardContent className='p-0'>
                                {transactionsLoading ? (
                                    <div className='flex items-center justify-center py-12'>
                                        <Loader2 className='h-5 w-5 animate-spin' />
                                    </div>
                                ) : !transactionsData || transactionsData.data.length === 0 ? (
                                    <p className='text-sm text-muted-foreground text-center py-12'>
                                        Keine Transaktionen vorhanden.
                                    </p>
                                ) : (
                                    <>
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Datum</TableHead>
                                                    <TableHead>Typ</TableHead>
                                                    <TableHead className='text-right'>Betrag</TableHead>
                                                    <TableHead>Details</TableHead>
                                                    <TableHead>Durchgeführt von</TableHead>
                                                    <TableHead className='w-10' />
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {transactionsData.data.map((tx) => {
                                                    const amount = Number(tx.amountEur);
                                                    const isPositive = amount >= 0;
                                                    const badgeConfig = TRANSACTION_TYPE_BADGES[tx.type] ?? { variant: 'outline' as const };

                                                    return (
                                                        <TableRow key={tx.id}>
                                                            <TableCell className='text-sm whitespace-nowrap'>
                                                                {format(new Date(tx.createdAt), 'dd.MM.yyyy HH:mm', { locale: de })}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Badge variant={badgeConfig.variant} className='text-xs'>
                                                                    {TRANSACTION_TYPE_LABELS[tx.type] ?? tx.type}
                                                                </Badge>
                                                            </TableCell>
                                                            <TableCell className={`text-right font-mono font-medium whitespace-nowrap ${isPositive ? 'text-green-600' : 'text-destructive'}`}>
                                                                {isPositive ? '+ ' : '− '}{formatCurrency(Math.abs(amount))}
                                                            </TableCell>
                                                            <TableCell className='text-sm'>
                                                                {tx.type === 'WITHDRAWAL' && tx.withdrawalReason && (
                                                                    <span className='text-muted-foreground'>{tx.withdrawalReason}</span>
                                                                )}
                                                                {tx.type === 'PAYMENT' && tx.payment && (() => {
                                                                    const payment = tx.payment;
                                                                    return (
                                                                        <div className='space-y-0.5'>
                                                                            <span
                                                                                className='text-primary cursor-pointer hover:underline font-mono text-xs'
                                                                                onClick={() => navigate(`/payment/${payment.id}`)}
                                                                            >
                                                                                Zahlung öffnen
                                                                            </span>
                                                                            {payment.billingDraft && (
                                                                                <span
                                                                                    className='block text-primary cursor-pointer hover:underline font-mono text-xs'
                                                                                    onClick={() => navigate(`/billing-draft/${payment.billingDraftId}`)}
                                                                                >
                                                                                    Auftrag {payment.billingDraft.billingDraftNumber}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })()}
                                                                {tx.type === 'TRANSFER_TO_OTHER_CASH_REGISTER' && tx.cashRegisterTo && (() => {
                                                                    const registerTo = tx.cashRegisterTo;
                                                                    return (
                                                                        <span
                                                                            className='text-primary cursor-pointer hover:underline'
                                                                            onClick={() => navigate(`/cash-register/${registerTo.id}`)}
                                                                        >
                                                                            → {registerTo.name}
                                                                        </span>
                                                                    );
                                                                })()}
                                                                {tx.type === 'TRANSFER_FROM_OTHER_CASH_REGISTER' && tx.cashRegisterFrom && (() => {
                                                                    const registerFrom = tx.cashRegisterFrom;
                                                                    return (
                                                                        <span
                                                                            className='text-primary cursor-pointer hover:underline'
                                                                            onClick={() => navigate(`/cash-register/${registerFrom.id}`)}
                                                                        >
                                                                            ← {registerFrom.name}
                                                                        </span>
                                                                    );
                                                                })()}
                                                                {tx.note && (
                                                                    <span className='block text-xs text-muted-foreground italic mt-0.5'>
                                                                        {tx.note}
                                                                    </span>
                                                                )}
                                                            </TableCell>
                                                            <TableCell className='text-sm whitespace-nowrap'>
                                                                {tx.createdByUser
                                                                    ? `${tx.createdByUser.firstName} ${tx.createdByUser.lastName}`
                                                                    : '—'}
                                                            </TableCell>
                                                            <TableCell>
                                                                {tx.type === 'WITHDRAWAL' && (
                                                                    <Button
                                                                        variant='ghost'
                                                                        size='icon'
                                                                        className='h-7 w-7 text-destructive hover:text-destructive'
                                                                        onClick={() => setUndoWithdrawalTarget({
                                                                            id: tx.id,
                                                                            amountEur: Math.abs(amount),
                                                                            reason: tx.withdrawalReason ?? '',
                                                                        })}
                                                                    >
                                                                        <X className='h-4 w-4' />
                                                                    </Button>
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>

                                        {transactionsData.pageCount > 1 && (
                                            <div className='flex items-center justify-between p-4 border-t'>
                                                <p className='text-sm text-muted-foreground'>
                                                    Seite {page + 1} von {transactionsData.pageCount} ({transactionsData.totalCount} Einträge)
                                                </p>
                                                <div className='flex items-center gap-2'>
                                                    <Button
                                                        variant='outline'
                                                        size='sm'
                                                        disabled={page === 0}
                                                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                                                    >
                                                        <ChevronLeft className='h-4 w-4' />
                                                    </Button>
                                                    <Button
                                                        variant='outline'
                                                        size='sm'
                                                        disabled={page >= transactionsData.pageCount - 1}
                                                        onClick={() => setPage((p) => p + 1)}
                                                    >
                                                        <ChevronRight className='h-4 w-4' />
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </CardContent>
                        </TabsContent>

                        {/* Tagesabschluss Tab */}
                        <TabsContent value='statements'>
                            <CardContent className='p-0'>
                                {statementsLoading ? (
                                    <div className='flex items-center justify-center py-12'>
                                        <Loader2 className='h-5 w-5 animate-spin' />
                                    </div>
                                ) : !statementsOverview || statementsOverview.data.length === 0 ? (
                                    <p className='text-sm text-muted-foreground text-center py-12'>
                                        Keine Tagesabschlüsse vorhanden.
                                    </p>
                                ) : (
                                    <>
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Datum</TableHead>
                                                    <TableHead>Status</TableHead>
                                                    <TableHead>Abgeschlossen von</TableHead>
                                                    <TableHead>Abschlusszeit</TableHead>
                                                    <TableHead className='text-right'>Differenz</TableHead>
                                                    <TableHead className='text-right'>Versionen</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {statementsOverview.data.map((s) => (
                                                    <TableRow
                                                        key={s.date}
                                                        className='cursor-pointer hover:bg-muted/50'
                                                        onClick={() => navigate(`/cash-register/${id}/statement?date=${s.date}`)}
                                                    >
                                                        <TableCell className='text-sm font-medium whitespace-nowrap'>
                                                            {format(new Date(s.date + 'T00:00:00'), 'dd.MM.yyyy', { locale: de })}
                                                        </TableCell>
                                                        <TableCell>
                                                            {s.isClosed ? (
                                                                <Badge variant='default' className='text-xs'>
                                                                    <Lock className='mr-1 h-3 w-3' />
                                                                    Abgeschlossen
                                                                </Badge>
                                                            ) : (
                                                                <Badge variant='outline' className='text-xs'>Offen</Badge>
                                                            )}
                                                        </TableCell>
                                                        <TableCell className='text-sm whitespace-nowrap'>
                                                            {s.closedByUser
                                                                ? `${s.closedByUser.firstName} ${s.closedByUser.lastName}`
                                                                : '—'}
                                                        </TableCell>
                                                        <TableCell className='text-sm whitespace-nowrap'>
                                                            {s.closedAt
                                                                ? format(new Date(s.closedAt), 'dd.MM.yyyy HH:mm', { locale: de })
                                                                : '—'}
                                                        </TableCell>
                                                        <TableCell className={`text-right font-mono font-medium whitespace-nowrap ${s.differenceEur === 0 ? 'text-green-600' : 'text-destructive'}`}>
                                                            {s.differenceEur === 0 ? '' : s.differenceEur > 0 ? '+ ' : '− '}
                                                            {formatCurrency(Math.abs(s.differenceEur))}
                                                        </TableCell>
                                                        <TableCell className='text-right text-sm'>
                                                            {s.versionCount}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>

                                        {statementsOverview.pageCount > 1 && (
                                            <div className='flex items-center justify-between p-4 border-t'>
                                                <p className='text-sm text-muted-foreground'>
                                                    Seite {statementsPage + 1} von {statementsOverview.pageCount} ({statementsOverview.totalCount} Einträge)
                                                </p>
                                                <div className='flex items-center gap-2'>
                                                    <Button
                                                        variant='outline'
                                                        size='sm'
                                                        disabled={statementsPage === 0}
                                                        onClick={() => setStatementsPage((p) => Math.max(0, p - 1))}
                                                    >
                                                        <ChevronLeft className='h-4 w-4' />
                                                    </Button>
                                                    <Button
                                                        variant='outline'
                                                        size='sm'
                                                        disabled={statementsPage >= statementsOverview.pageCount - 1}
                                                        onClick={() => setStatementsPage((p) => p + 1)}
                                                    >
                                                        <ChevronRight className='h-4 w-4' />
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </CardContent>
                        </TabsContent>
                    </Tabs>
                </Card>
            </div>

            {/* Edit Dialog */}
            {cashRegister && (
                <EditCashRegisterDialog
                    open={showEditDialog}
                    onOpenChange={setShowEditDialog}
                    cashRegisterId={cashRegister.id}
                    storeId={cashRegister.storeId}
                    anotherRegisterIsDefault={anotherRegisterIsDefault}
                    currentDefaultName={currentDefaultCashRegisterName}
                    defaultValues={{
                        name: cashRegister.name,
                        isActive: cashRegister.isActive,
                        requiresDailyCashStatement: cashRegister.requiresDailyCashStatement,
                        isDefaultForStore: cashRegister.isDefaultForStore,
                    }}
                />
            )}

            {/* Undo Withdrawal confirmation dialog */}
            <AlertDialog open={!!undoWithdrawalTarget} onOpenChange={(open) => { if (!open) setUndoWithdrawalTarget(null); }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Entnahme rückgängig machen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Möchten Sie die Entnahme{undoWithdrawalTarget?.reason ? ` "${undoWithdrawalTarget.reason}"` : ''} über{' '}
                            <strong>{formatCurrency(undoWithdrawalTarget?.amountEur ?? 0)}</strong>{' '}
                            wirklich rückgängig machen? Der Betrag wird der Kasse wieder gutgeschrieben.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={undoWithdrawalMutation.isPending}>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                if (undoWithdrawalTarget) {
                                    undoWithdrawalMutation.mutate({
                                        cashRegisterId: id!,
                                        amountEur: undoWithdrawalTarget.amountEur,
                                        note: `Storno der Entnahme${undoWithdrawalTarget.reason ? `: ${undoWithdrawalTarget.reason}` : ''}`,
                                    });
                                }
                            }}
                            disabled={undoWithdrawalMutation.isPending}
                        >
                            {undoWithdrawalMutation.isPending && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                            Rückgängig machen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Content>
    );
};

export default CashRegister;
